import { useState } from 'react'

import { Box } from '@enechain/ecloud-designsystem'

import { DisclosureIcon } from '~/components/common/AccordionDisclosureIcon'

type Props = {
  /** ヘッダーに出す見出し。 */
  title: React.ReactNode
  /** 見出しの後ろに付ける標 (入札制限マトリクスの「編集中」等)。 */
  badge?: React.ReactNode
  /** 既定で開くか。 既定 = 開いた状態。 */
  defaultOpen?: boolean
  /** カード間の余白。 一覧の最後のカードには付けない。 */
  marginBottom?: number
  children: React.ReactNode
}

/**
 * 枠付きカードのアコーディオン。
 *
 * ## なぜ共通化したか
 *
 * 入札制限設定の 5 枚のカードが同じ組み方を各タブに写しており、 **開閉標が
 * どれにも無かった**。 `<details>` は `list-style` を消さないとブラウザが既定の
 * 開閉標 (Chrome/Safari では黒い ▶) を描くため、 DS のどのアイコンとも似ていない
 * 三角が出ていた (2026-09-02 デザイナー指摘)。
 *
 * ## 開閉標
 *
 * `DisclosureIcon` を使う。 8080 の `.ecl-accordion-trigger::before`
 * (`css/components.css:4238-4250`) と同じく **閉 = chevron-right / 開 =
 * chevron-down のグリフ差し替え**で、 回転ではない (回転だと閉じている時に
 * 上向き ▲ に見える)。 器・寸法は 2026-08-31 の「アコーディオン基準統一」
 * (承認フロー `ProcessAccordion` を基準とするデザイナー判断) に合わせる。
 *
 * ## なぜ `<button>` ではなく `<details>` のままか
 *
 * 基準の `ProcessAccordion` は `useState` + `<button>` だが、 こちらは既に
 * `<details>/<summary>` で組まれており、 開閉状態と `aria-expanded` を
 * ブラウザが持つぶん素直。 見た目だけ基準へ揃え、 要素は変えない。
 * 開閉標は React 側で描くため、 `open` の変化を `onToggle` で拾って state に写す。
 */
export const CardAccordion = ({
  title,
  badge,
  defaultOpen = true,
  marginBottom,
  children,
}: Props): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    // 器は 8080 `.ecl-accordion` (bid-limits.html の各カードも同 class) = 枠 1px
    // Border.lowEmphasis / 角丸 lg / 白地 / overflow hidden。
    <Box
      overflow="hidden"
      marginBottom={marginBottom}
      borderWidth={1}
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      asChild
      backgroundColor="Surface.primary"
    >
      <details
        onToggle={(e) => {
          setIsOpen(e.currentTarget.open)
        }}
        open={isOpen}
      >
        <Box
          as="summary"
          textStyle="accordionTitle"
          alignItems="center"
          display="flex"
          _hover={{ bg: 'Surface.secondary' }}
          cursor="pointer"
          // 次の 2 つ (`css` の webkit 疑似要素 / `listStyleType`) が
          // ブラウザ既定の開閉標を消す指定。 `list-style` だけだと Safari の
          // `::-webkit-details-marker` が残るので両方要る
          // (8080 `.ecl-accordion-trigger` も同じ 2 つを持つ)。
          css={{ '&::-webkit-details-marker': { display: 'none' } }}
          listStyleType="none"
          paddingX={4}
          paddingY={4}
        >
          <DisclosureIcon expanded={isOpen} />
          {title}
          {badge}
        </Box>
        <Box
          borderTopWidth="1px"
          borderTopColor="Border.lowEmphasis"
          paddingX={4}
          paddingY={4}
        >
          {children}
        </Box>
      </details>
    </Box>
  )
}
